import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import type { AIModel, ModelType } from '../types/models';
import { translations } from '../utils/translations';
import { analyzePromptWithGroq, selectModel as routeModel, RouterResult } from '../utils/llmRouter';
import { MODELS_DATABASE, getModelInfo, convertLegacyType } from '../config/modelsDatabase';

const getProviderTranslation = (provider: string): string => {
  const translations: Record<string, string> = {
    'openai': 'OpenAI',
    'anthropic': 'Anthropic',
    'groq': 'Groq',
    'gemini': 'Gemini',
    'deepseek': 'DeepSeek',
    'minimax': 'Minimax',
    'cerebras': 'Cerebras',
    'qwen': 'Qwen',
    'mistral': 'Mistral',
    'xai': 'xAI'
  };
  return translations[provider] || provider;
};

interface ModelBadgeProps {
  type: string;
  small?: boolean;
}

const ModelBadge: React.FC<ModelBadgeProps> = ({ type, small }) => {
  const getTypeStyles = (type: string) => {
    switch (type) {
      case 'Basic':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-blue-100 text-blue-800';
      case 'Expert':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span
      className={`
        ${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}
        rounded-full font-medium
        ${getTypeStyles(type)}
      `}
    >
      {type}
    </span>
  );
};

export const ModelSelector: React.FC = () => {
  const { selectedModel, selectModel, language, apiConfigs, topUpValue } = useStore();
  const [filter, setFilter] = useState<ModelType | 'All'>('All');
  const [showFilters, setShowFilters] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showMobileModels, setShowMobileModels] = useState(false);
  const [recommendation, setRecommendation] = useState<RouterResult | null>(null);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredModel, setHoveredModel] = useState<AIModel | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout>();

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleModelHover = (model: AIModel | null) => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    setHoveredModel(model);

    // If a model is being hovered, set a timeout to clear it after 10 seconds
    if (model) {
      tooltipTimeoutRef.current = setTimeout(() => {
        setHoveredModel(null);
      }, 5000); // 5 seconds
    }
  };

  // Get system API keys availability
  const getSystemApiKeyAvailable = (provider: string): boolean => {
    const providerMap: Record<string, string> = {
      'anthropic': 'ANTHROPIC',
      'openai': 'OPENAI',
      'groq': 'GROQ',
      'gemini': 'GEMINI',
      'deepseek': 'DEEPSEEK',
      'minimax': 'MINIMAX',
      'cerebras': 'CEREBRAS',
      'qwen': 'QWEN',
      'mistral': 'MISTRAL',
      'xai': 'XAI'
    };

    const normalizedProvider = providerMap[provider] || provider.toUpperCase();
    const envVar = `VITE_SYSTEM_${normalizedProvider}_API_KEY`;
    return !!import.meta.env[envVar];
  };

  // Check if a model is usable (has user API or has enough topUpValue)
  const isModelUsable = (model: AIModel): boolean => {
    const hasUserApiKey = apiConfigs.some(config => config.provider === model.provider);
    if (hasUserApiKey) return true;
    
    const hasSystemApi = getSystemApiKeyAvailable(model.provider);
    return hasSystemApi && topUpValue > 0;
  };

  // Convert models database to AIModel array
  const models: AIModel[] = Object.values(MODELS_DATABASE).map(model => ({
    ...model,
    isConfigured: true,
    systemApiCost: model.pricing.prompt_tokens_per_1k * 1000 // Convert to per 1M tokens
  }));

  // Filter models based on type and API availability
  const filteredModels = models.filter(model => {
    if (filter !== 'All' && !model.type.includes(filter)) {
      return false;
    }
    
    return isModelUsable(model);
  });

  const handleModelChange = (model: AIModel) => {
    const hasApiKey = apiConfigs.some(config => config.provider === model.provider);
    const hasSystemApi = getSystemApiKeyAvailable(model.provider);
    
    if (!hasApiKey && (!hasSystemApi || topUpValue <= 0)) {
      setError(language === 'es'
        ? hasSystemApi
          ? 'Necesitas añadir fondos para usar este modelo'
          : `Necesitas configurar una clave API para ${getProviderTranslation(model.provider)}`
        : hasSystemApi
          ? 'You need to add funds to use this model'
          : `You need to configure an API key for ${getProviderTranslation(model.provider)}`);
      return;
    }

    selectModel(model);
    setShowModelDropdown(false);
    setShowRecommendation(false);
    setError(null);
  };

  // Create a secure method to get API key using a function with clear rate limiting
  const getSystemApiKey = (() => {
    // Simple rate limiting to prevent abuse
    let lastCallTime = 0;
    let callCount = 0;
    const RATE_LIMIT_RESET_TIME = 60000; // 1 minute
    const MAX_CALLS_PER_MINUTE = 5;
    
    return (): string | null => {
      const now = Date.now();
      
      // Reset count if enough time has passed
      if (now - lastCallTime > RATE_LIMIT_RESET_TIME) {
        callCount = 0;
        lastCallTime = now;
      }
      
      // Check rate limiting
      if (callCount >= MAX_CALLS_PER_MINUTE) {
        console.warn('Rate limit exceeded for API key access');
        return null;
      }
      
      callCount++;
      lastCallTime = now;
      
      // Get key from environment variable, but never expose it directly
      const key = import.meta.env.VITE_SYSTEM_GROQ_API_KEY;
      return key ? String(key) : null;
    };
  })();
  
  // Helper for safely getting input content with sanitization
  const getSanitizedInputContent = (): string | null => {
    // Find the input element safely
    const chatInput = document.querySelector('textarea[placeholder*="Type"], textarea[placeholder*="Escribe"]') as HTMLTextAreaElement | null;
    
    // Validate the element exists and has content
    if (!chatInput || !chatInput.value || typeof chatInput.value !== 'string') {
      return null;
    }
    
    const content = chatInput.value.trim();
    
    // Basic validation for content
    if (!content || content.length < 3 || content.length > 2000) {
      return null;
    }
    
    return content;
  };

  const handleRecommendation = async () => {
    // Get and validate input content
    const inputContent = getSanitizedInputContent();
    if (!inputContent) {
      setError(language === 'es' ? 'Por favor, escribe un mensaje válido primero' : 'Please type a valid message first');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Get API key securely with rate limiting
      const systemApiKey = getSystemApiKey();
      if (!systemApiKey) {
        throw new Error(language === 'es'
          ? 'Demasiadas solicitudes. Inténtalo de nuevo más tarde.'
          : 'Too many requests. Please try again later.');
      }

      // Get models list safely
      const validModelIds = models
        .filter(m => m && typeof m.id === 'string')
        .map(m => m.id);
      
      const result = await analyzePromptWithGroq(inputContent, { apiKey: systemApiKey });
      const routedResult = routeModel(result, validModelIds);
      
      // Validate the result before setting state
      if (!routedResult || !routedResult.detectedCapability || !routedResult.performanceModel) {
        throw new Error(language === 'es'
          ? 'Respuesta de análisis inválida'
          : 'Invalid analysis response');
      }
      
      setError(null);
      setRecommendation(routedResult);
      setShowRecommendation(true);
    } catch (error) {
      console.error('Error getting model recommendation:', 
        error instanceof Error 
          ? `Type: ${error.name}, Message: ${error.message.replace(/key|api|token|secret/gi, '[REDACTED]')}`
          : 'Unknown error type');
      
      // Don't expose raw error messages to users
      setError(language === 'es'
        ? 'No se pudo obtener la recomendación en este momento. Inténtalo de nuevo más tarde.'
        : 'Could not get recommendation at this time. Please try again later.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const allTypes: (ModelType | 'All')[] = ['All', 'Basic', 'Medium', 'Expert'];

  return (
    <div className="w-full max-w-md md:max-w-lg">
      {/* Mobile toggle button */}
      <div className="md:hidden flex justify-center">
        <button
          onClick={() => setShowMobileModels(!showMobileModels)}
          className="flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
          aria-label={showMobileModels ? 'Hide Models' : 'Show Models'}
        >
          <span className="text-xs">Models</span>
          <svg
            className={`h-3 w-3 transition-transform ${
              showMobileModels ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Model selector content */}
      <div className={showMobileModels ? 'block' : 'hidden md:block'}>
        <div className="relative group">
          {/* Model selector and icons */}
          <div className={`flex items-center gap-1 ${!showMobileModels ? 'hidden md:flex' : 'flex'}`}>
            <button
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              className="relative w-full pl-6 pr-6 py-1 border rounded-lg bg-white shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {selectedModel ? (
                <div
                  className="flex flex-col md:flex-row md:items-center"
                  onMouseEnter={() => handleModelHover(selectedModel)}
                  onMouseLeave={() => handleModelHover(null)}
                >
                  <div className="relative flex items-center group">
                    <img src={selectedModel.icon} alt={selectedModel.name} className="w-4 h-4 object-contain mr-1" />
                    <span className="text-xs font-medium text-gray-900">{selectedModel.name}</span>
                    {hoveredModel?.id === selectedModel.id && (
                      <div className="fixed right-[60px] top-[80px] w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-[9999] border border-gray-700 pointer-events-none">
                        <div className="relative">
                          <h4 className="font-semibold mb-2 text-gray-200">
                            {/* Sanitize model name */}
                            {typeof selectedModel.name === 'string' ? 
                              selectedModel.name.slice(0, 50).replace(/</g, '&lt;').replace(/>/g, '&gt;') : 
                              'Unknown model'}
                          </h4>
                          {selectedModel.description ? (
                            <p className="text-gray-300 leading-relaxed">
                              {/* Sanitize description and limit length */}
                              {typeof selectedModel.description === 'string' 
                                ? selectedModel.description
                                  .replace(/</g, '&lt;')
                                  .replace(/>/g, '&gt;')
                                  .slice(0, 200) 
                                : 'No description available'}
                            </p>
                          ) : (
                            <p className="text-gray-400 leading-relaxed italic">
                              No description available
                            </p>
                          )}
                          <div className="absolute w-3 h-3 bg-gray-900 transform rotate-45 top-4 -left-[6px] border-l border-b border-gray-700"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 md:mt-0 md:absolute md:right-8 flex gap-1">
                    {selectedModel.type.map(type => (
                      <ModelBadge key={type} type={type} small />
                    ))}
                  </div>
                </div>
              ) : (
                <span className="text-gray-400">{translations[language].selectModelToStart}</span>
              )}

              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    showModelDropdown ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={showFilters ? "Hide filters" : "Show filters"}
              aria-expanded={showFilters}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
              </svg>
            </button>

            <button
              onClick={handleRecommendation}
              disabled={isAnalyzing}
              className={`p-1.5 rounded-full hover:bg-gray-100 transition-colors ${
                isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={language === 'es' ? 'Obtener recomendación de modelo' : 'Get model recommendation'}
            >
              {isAnalyzing ? (
                <svg
                  className="animate-spin h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-yellow-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                </svg>
              )}
            </button>
          </div>

          {error && (
            <div className="absolute top-full right-0 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
              {error}
            </div>
          )}

          {showRecommendation && recommendation && (
            <div className="absolute top-full right-0 mt-2 p-4 bg-white border rounded-lg shadow-lg z-30 w-80">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Recomendación de Modelo:' : 'Model Recommendation:'}
                  </span>
                  <button
                    onClick={() => setShowRecommendation(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="text-xs text-gray-500">
                  {language === 'es' ? 'Capacidad detectada:' : 'Detected capability:'} 
                  <span className="font-medium text-blue-600 ml-1">
                    {recommendation.detectedCapability === 'writing' ? 
                      (language === 'es' ? 'escritura' : 'writing') :
                     recommendation.detectedCapability === 'coding' ? 
                      (language === 'es' ? 'programación' : 'coding') :
                     recommendation.detectedCapability === 'reasoning' ? 
                      (language === 'es' ? 'razonamiento' : 'reasoning') :
                     recommendation.detectedCapability === 'math' ? 
                      (language === 'es' ? 'matemáticas' : 'math') :
                     recommendation.detectedCapability === 'speed' ? 
                      (language === 'es' ? 'velocidad' : 'speed') :
                     recommendation.detectedCapability === 'common sense' ? 
                      (language === 'es' ? 'sentido común' : 'common sense') :
                     recommendation.detectedCapability === 'multilingual' ? 
                      (language === 'es' ? 'multilingüe' : 'multilingual') :
                     recommendation.detectedCapability}
                  </span>
                  <span className="ml-2">({Math.round(recommendation.confidence * 100)}% {language === 'es' ? 'confianza' : 'confidence'})</span>
                </div>

                {/* Performance Model Section */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-sm font-medium text-[#1E3A8A]">
                    {language === 'es' ? 'Mejor Rendimiento:' : 'Best Performance:'}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-semibold text-[#1E3A8A]">
                      {recommendation.performanceModel.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{recommendation.performanceModel.reason}</p>
                </div>

                {/* Cost-Efficiency Model Section */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-sm font-medium text-[#9D174D]">
                    {language === 'es' ? 'Mejor Coste-Eficiencia:' : 'Best Cost-Efficiency:'}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-semibold text-[#9D174D]">
                      {recommendation.costEfficiencyModel.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{recommendation.costEfficiencyModel.reason}</p>
                </div>

                {/* Action Buttons */}
                <div className="mt-3 flex gap-2">
                  {(() => {
                    const findMatchingModel = (modelName: string) => {
                      return models.find(m => m.name === modelName);
                    };

                    const performanceModel = findMatchingModel(recommendation.performanceModel.name);
                    const costEfficiencyModel = findMatchingModel(recommendation.costEfficiencyModel.name);

                    return (
                      <>
                        {performanceModel && (
                          <button
                            onClick={() => handleModelChange(performanceModel)}
                            className="flex-1 px-3 py-1.5 bg-[#1E3A8A] text-white text-sm rounded-md hover:bg-[#1E40AF] transition-colors shadow-sm"
                          >
                            {language === 'es' ? 'Usar Mejor Rendimiento' : 'Use Best Performance'}
                          </button>
                        )}
                        {costEfficiencyModel && (
                          <button
                            onClick={() => handleModelChange(costEfficiencyModel)}
                            className="flex-1 px-3 py-1.5 border border-[#9D174D] bg-[#FDF2F8] text-[#9D174D] text-sm rounded-md hover:bg-[#FCE7F3] transition-colors"
                          >
                            {language === 'es' ? 'Usar Coste-Eficiente' : 'Use Cost-Efficient'}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Alternative Models */}
                {recommendation.alternativeModels && recommendation.alternativeModels.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-700">
                      {language === 'es' ? 'Alternativas:' : 'Alternatives:'}
                    </div>
                    <div className="mt-2 space-y-2">
                      {recommendation.alternativeModels.map((altModel, index) => {
                        const model = models.find(m => m.name === altModel.name);
                        if (!model) return null;
                        
                        return (
                          <div key={index} className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-1">
                              <img src={model.icon} alt={model.name} className="w-3 h-3 object-contain" />
                              <span className="font-medium">{altModel.name}</span>
                              <span className="text-gray-500">({Math.round(altModel.score * 100)}%)</span>
                            </div>
                            <button
                              onClick={() => handleModelChange(model)}
                              className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                            >
                              {language === 'es' ? 'Usar' : 'Use'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {showFilters && (
            <div className="absolute top-full right-0 mt-2 p-4 bg-white border rounded-lg shadow-lg z-10">
              <div className="flex flex-wrap gap-2">
                {allTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => {
                      setFilter(type);
                      setShowFilters(false);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                      filter === type
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showModelDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
              {filteredModels.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {language === 'es' ? 'No hay modelos disponibles' : 'No models available'}
                </div>
              ) : filteredModels.map((model) => (
                <div
                  key={model.id}
                  onClick={() => isModelUsable(model) && handleModelChange(model)}
                  onMouseEnter={() => handleModelHover(model)}
                  onMouseLeave={() => handleModelHover(null)}
                  className={`relative flex items-center justify-between px-3 py-2 group ${
                    isModelUsable(model)
                      ? 'hover:bg-gray-50 cursor-pointer'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  {hoveredModel?.id === model.id && (
                    <div className="fixed right-[60px] top-[80px] w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-[9999] border border-gray-700 pointer-events-none">
                      <div className="relative">
                        <h4 className="font-semibold mb-2 text-gray-200">{model.name}</h4>
                        {model.description ? (
                          <p className="text-gray-300 leading-relaxed">
                            {model.description}
                          </p>
                        ) : (
                          <p className="text-gray-400 leading-relaxed italic">
                            No description available
                          </p>
                        )}
                        <div className="absolute w-3 h-3 bg-gray-900 transform rotate-45 top-4 -left-[6px] border-l border-b border-gray-700"></div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <img src={model.icon} alt={model.name} className="w-5 h-5 object-contain" />
                    <div>
                      <span className="text-sm text-gray-900">{model.name}</span>
                      {!isModelUsable(model) && (
                        <div className="text-xs text-red-500">
                          {language === 'es'
                            ? (topUpValue <= 0 && getSystemApiKeyAvailable(model.provider))
                              ? 'Requiere fondos'
                              : 'Requiere API key'
                            : (topUpValue <= 0 && getSystemApiKeyAvailable(model.provider))
                              ? 'Requires funds'
                              : 'Requires API key'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {model.type.map(type => (
                        <ModelBadge key={type} type={type} small />
                      ))}
                    </div>
                    {apiConfigs.some(config => config.provider === model.provider) ? (
                      <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                        {language === 'es' ? 'API Personal' : 'Personal API'}
                      </span>
                    ) : getSystemApiKeyAvailable(model.provider) ? (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        topUpValue > 0
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-500 bg-gray-100'
                      }`}>
                        {language === 'es' ? 'API Sistema' : 'System API'}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {language === 'es' ? 'Sin API' : 'No API'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};